import * as fs from 'fs';
import * as PATH from 'path';

export enum NodeType {
  DIRECTORY = 'directory',
  FILE = 'file'
}

function safeReadDirSync (path: string): string[] | null {
  let dirData = [];
  try {
    dirData = fs.readdirSync(path) as string[];
  } catch(ex) {
    if (ex.code == "EACCES" || ex.code == "EPERM") {
      //User does not have permissions, ignore directory
      return null;
    }
    else throw ex;
  }
  return dirData;
}

/**
 * Normalizes windows style paths by replacing double backslahes with single forward slahes (unix style).
 * @param  {string} path
 * @return {string}
 */
function normalizePath(path: string): string {
  return path.replace(/\\/g, '/');
}

/**
 * Tests if the supplied parameter is of type RegExp
 * @param  {any}  regExp
 * @return {Boolean}
 */
function isRegExp(regExp: any): boolean {
  return typeof regExp === "object" && regExp.constructor == RegExp;
}

export type FileSystemTree = { type: NodeType, size?: number, path: string, name: string, isSymbolicLink?: boolean, children?: FileSystemTree[], extension?: string }
type DirectoryTreeOptions = {
  depth?: number,
  attributes?: string[],
  followSymlinks?: boolean,
  symlinks?: number[],
  extensions?: RegExp,
  exclude?: RegExp | RegExp[],
  normalizePath?: boolean,
}
/**
 * Collects the files and folders for a directory path into an Object, subject
 * to the options supplied, and invoking optional
 * @param  {String} path
 * @param  {Object} options
 * @param  {function} onEachFile
 * @param  {function} onEachDirectory
 * @return {Object}
 */
export function directoryTree (path: string, options?: DirectoryTreeOptions, onEachFile?: (item: FileSystemTree, path: string, stats: fs.Stats) => void, onEachDirectory?: (item: FileSystemTree, path: string, stats: fs.Stats) => void, currentDepth = 0) : FileSystemTree {
  options = options || {};

  if (options.depth !== undefined && options.attributes && options.attributes.indexOf('size') !== -1) {
    throw new Error('usage of size attribute with depth option is prohibited');
  }

  const name = PATH.basename(path);
  path = options.normalizePath ? normalizePath(path) : path;
  const item : FileSystemTree = { path, name, type: NodeType.DIRECTORY };
  let stats;
  let lstat;

  try {
    stats = fs.statSync(path);
    lstat = fs.lstatSync(path);
  }
  catch (e) { return null }

  // Skip if it matches the exclude regex
  if (options.exclude) {
    const excludes =  isRegExp(options.exclude) ? [options.exclude as RegExp] : options.exclude as RegExp[];
    if (excludes.some((exclusion) => exclusion.test(path))) {
      return null;
    }
  }

  if (lstat.isSymbolicLink()) {
    item.isSymbolicLink = true;
    // Skip if symbolic links should not be followed
    if (options.followSymlinks === false)
      return null;
    // Initialize the symbolic links array to avoid infinite loops
    if (!options.symlinks)
      options = { ...options, symlinks: [] };
    // Skip if a cyclic symbolic link has been found
    if (options.symlinks.find(ino => ino === lstat.ino)) {
      return null;
    } else {
      options.symlinks.push(lstat.ino);
    }
  }

  if (stats.isFile()) {

    const ext = PATH.extname(path).toLowerCase();

    // Skip if it does not match the extension regex
    if (options.extensions && !options.extensions.test(ext))
      return null;


    if (options.attributes) {
      options.attributes.forEach((attribute) => {
        switch (attribute) {
          case 'extension':
            item.extension = ext;
            break;
          case 'type':
            item.type = NodeType.FILE;
            break;
          default:
            item[attribute] = stats[attribute];
            break;
        }
      });
    }

    if (onEachFile) {
      onEachFile(item, path, stats);
    }
  }
  else if (stats.isDirectory()) {
    let dirData = safeReadDirSync(path);
    if (dirData === null) return null;

    if (options.depth === undefined || options.depth > currentDepth) {
      item.children = dirData
          .map(child => directoryTree(PATH.join(path, child), options, onEachFile, onEachDirectory, currentDepth + 1))
          .filter(e => !!e);
    }

    if (options.attributes) {
      options.attributes.forEach((attribute) => {
        switch (attribute) {
          case 'size':
            item.size = item.children.reduce((prev, cur) => prev + cur.size, 0);
            break;
          case 'type':
            item.type = NodeType.DIRECTORY;
            break;
          case 'extension':
            break;
          default:
            item[attribute] = stats[attribute];
            break;
        }
        
      });
    }

    if (onEachDirectory) {
      onEachDirectory(item, path, stats);
    }
  } else {
    return null; // Or set item.size = 0 for devices, FIFO and sockets ?
  }
  return item;
}


/**
 * The global a page sets to opt `AssetReader` into reading over `fetch`: the base URL its asset directories are served
 * under (`''` for the page's own origin). The playground's generated `index.html` sets it before any module runs; nothing
 * else should. It is read at every call, never at module load, and the environment is never sniffed instead: a stand-in
 * `window` (the CLI's node definition pass), node's own global `fetch` and a faked `process.versions.node` (the
 * playground's browser polyfill) each fool a sniff.
 */
export const ASSET_BASE_URL_GLOBAL = '__GOTTI_ASSET_BASE_URL__';

/** The base URL the page opted in with; `undefined` where nothing did (node, or a page that did not opt in). */
const pageAssetBaseUrl = (): string | undefined => {
  const value = (globalThis as Record<string, unknown>)[ASSET_BASE_URL_GLOBAL];
  return typeof value === 'string' ? value : undefined;
};

/**
 * `url` fetched; a rejection naming the URL when the server refused it (its status), or when it answered a file that is
 * not HTML with an HTML page — a dev server's answer for a path nothing serves (Vite's SPA fallback: `index.html`, 200).
 */
const fetchAsset = async (url: string): Promise<Response> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`AssetReader: ${url} answered ${response.status}`);
  const answeredHtml = /^text\/html\b/i.test(response.headers.get('content-type') ?? '');
  if (answeredHtml && !/\.html?$/i.test(new URL(url, 'http://asset.invalid').pathname)) {
    throw new Error(`AssetReader: ${url} answered the dev server's HTML page — is its directory listed in assetRoots?`);
  }
  return response;
};

/**
 * Reads a game's asset files. Under node it reads from disk below `assetPath`; in a page that opted in (see
 * `ASSET_BASE_URL_GLOBAL`) it reads `<assetPath>/<file>` over `fetch` — the asynchronous reads only, since a page has
 * no filesystem to read synchronously.
 */
export class AssetReader {
  /** The global a page sets to opt into `fetch` reads — the playground's generated page does; nothing else should. */
  static readonly ASSET_BASE_URL_GLOBAL = ASSET_BASE_URL_GLOBAL;

  readonly assetPath: string;
  readonly dirTree: FileSystemTree;
  constructor(assetPath: string) {
    this.assetPath = assetPath;
    // A page has no filesystem to walk: its tree is the directory itself, with nothing listed.
    this.dirTree = pageAssetBaseUrl() !== undefined
      ? { type: NodeType.DIRECTORY, path: assetPath, name: assetPath.replace(/\/+$/, '').split('/').pop() ?? assetPath, children: [] }
      : directoryTree(this.assetPath);
  }

  /**
   * `<assetPath>/<file>` as a URL; a leading `./` or `/` on the file names the same file. Each segment of the file's path
   * is percent-encoded: a `%`, `#`, `?` or space in a name is part of the name, not URL syntax.
   */
  private assetUrl(pathToFile: string): string {
    const segments = String(pathToFile).replace(/^(\.\/|\/)+/, '').split('/');
    return `${this.assetPath.replace(/\/+$/, '')}/${segments.map(encodeURIComponent).join('/')}`;
  }

  public getJSON(pathToFile: string) {
    if (pageAssetBaseUrl() !== undefined) throw new Error('AssetReader.getJSON is synchronous filesystem access; in the browser use getJSONAsync.');
    return JSON.parse(fs.readFileSync(PATH.resolve(this.assetPath, pathToFile), 'utf8'));
  }
  public getFile(pathToFile: string, encoding?: BufferEncoding) : any {
    if (pageAssetBaseUrl() !== undefined) throw new Error('AssetReader.getFile is synchronous filesystem access; in the browser use getFileAsync.');
    return fs.readFileSync(PATH.resolve(this.assetPath, pathToFile), encoding)
  }
  public async getJSONAsync(pathToFile: string): Promise<any> {
    if (pageAssetBaseUrl() !== undefined) return (await fetchAsset(this.assetUrl(pathToFile))).json();
    return new Promise((resolve, reject) => {
      fs.readFile(PATH.resolve(this.assetPath, pathToFile), 'utf8', (err, data) => {
        if (err) {
          reject(err);
          return;
        }
        try {
          resolve(JSON.parse(data));
        } catch (parseError) {
          reject(parseError);
        }
      });
    });
  }

  public async getFileAsync(pathToFile: string, encoding?: BufferEncoding): Promise<any> {
    if (pageAssetBaseUrl() !== undefined) {
      // Bytes, or utf8 text: `fetch` decodes nothing else, and text in another encoding than asked for is worse than none.
      if (encoding != null && !/^utf-?8$/i.test(encoding)) {
        throw new Error(`AssetReader.getFileAsync('${pathToFile}', '${encoding}'): a page decodes utf8 only — read the bytes (no encoding) and decode them yourself.`);
      }
      const response = await fetchAsset(this.assetUrl(pathToFile));
      return encoding == null ? new Uint8Array(await response.arrayBuffer()) : response.text();
    }
    return new Promise((resolve, reject) => {
        // for some reason passing in an undefined encoding value does not act the same 
      // as omitting it. So we need to check for undefined and omit the encoding parameter ourselves
      const callback = (err: NodeJS.ErrnoException | null, data: Buffer | string) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(data);
      };
  
      if (encoding === undefined) {
        fs.readFile(PATH.resolve(this.assetPath, pathToFile), callback);
      } else {
        fs.readFile(PATH.resolve(this.assetPath, pathToFile), encoding, callback);
      }
    });
  }

  /**
   * The nearest directory named `name`, searching `curDir` (default: this module's directory) and then each ancestor.
   * In a page that opted in, `${base}/${name}` — where the playground serves an `assetRoots` directory of that name.
   */
  public static resolveFirstAncestorDirectory(
    name: string,
    // A default parameter is evaluated before the body: a bare `__dirname` here would throw in a page (which has none)
    // before the opt-in below could answer, so it is touched only where the module system defines it.
    curDir: string | undefined = typeof __dirname !== 'undefined' ? __dirname : undefined,
    ignoreCasing = false,
  ): string {
    const base = pageAssetBaseUrl();
    if (base !== undefined) return `${base}/${name}`;
    if (curDir === undefined) {
      throw new Error(
        `AssetReader.resolveFirstAncestorDirectory('${name}') has no directory to search up from: this module has no __dirname here. ` +
          `Pass one (e.g. path.dirname(fileURLToPath(import.meta.url))); in a page, set globalThis.${ASSET_BASE_URL_GLOBAL} to read over fetch.`,
      );
    }
    // recursively check from current directory until we find the directory by stepping backwards
    let currentDirectory = curDir;

    while (true) {
      // Get all entries in the current directory
      const entries = fs.readdirSync(currentDirectory);
      
      // Check if any entry matches our target name, respecting case sensitivity setting
      const found = entries.find(entry => {
        if (!ignoreCasing) {
          return entry === name;
        } else {
          return entry.toLowerCase() === name.toLowerCase();
        }
      });
      
      if (found !== undefined) {
        return PATH.join(currentDirectory, found as string);
      }
      
      // Move up to parent directory
      const parentDir = PATH.dirname(currentDirectory);
      if (parentDir === currentDirectory) {
        throw new Error(`Could not find any parent directory with name: ${name}`);
      }
      currentDirectory = parentDir;
    }
  }
}